import React, { useEffect, useState } from 'react';
import AppLayout from '../../components/layout/AppLayout';
import { PageHeader, KpiCard } from '../../components/common/PageHeader';
import api from '../../lib/api';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import {
  TrendingUp, Briefcase, Euro, PieChart as PieIcon,
  Users, UserCog, BarChart3, Calendar
} from 'lucide-react';
import { statusLabel, serviceTypeLabel } from '../../lib/constants';

const PRIMARY = '#0F4C81';
const SUCCESS = '#10B981';
const WARNING = '#F59E0B';
const DANGER = '#EF4444';
const SLATE = '#64748B';
const CHART_COLORS = [PRIMARY, '#0EA5E9', '#10B981', '#F59E0B', '#EC4899', '#8B5CF6', '#EF4444', '#14B8A6'];

const fmtEur = (v) => `${(v || 0).toFixed(2)} €`;

const AdminReports = () => {
  const [data, setData] = useState(null);
  const [months, setMonths] = useState(6);

  useEffect(() => {
    api.get(`/admin/reports/overview?months=${months}`).then(r => setData(r.data));
  }, [months]);

  if (!data) return <AppLayout><div>Lade...</div></AppLayout>;

  const { months: monthsData, top_languages, service_distribution, status_distribution,
    top_customers, top_interpreters, totals } = data;

  const serviceDistData = service_distribution.map(s => ({
    name: serviceTypeLabel(s.type),
    value: s.count,
  }));

  const statusDistData = status_distribution.map(s => ({
    name: statusLabel(s.status),
    value: s.count,
  })).sort((a, b) => b.value - a.value);

  return (
    <AppLayout>
      <PageHeader title="Statistiken & Reports" description="Auswertungen über Aufträge, Sprachen und Marge">
        <select
          value={months}
          onChange={(e) => setMonths(parseInt(e.target.value))}
          data-testid="months-selector"
          className="border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F4C81] outline-none"
        >
          <option value={3}>Letzte 3 Monate</option>
          <option value={6}>Letzte 6 Monate</option>
          <option value={12}>Letzte 12 Monate</option>
        </select>
      </PageHeader>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
        <KpiCard label="Gesamtaufträge" value={totals.total_jobs} icon={Briefcase} testid="kpi-total-jobs" />
        <KpiCard label="Abgeschlossen" value={totals.completed_jobs} icon={Calendar} accent="text-emerald-600" testid="kpi-completed" />
        <KpiCard label="Umsatz (gesamt)" value={fmtEur(totals.total_revenue)} icon={Euro} testid="kpi-revenue" />
        <KpiCard label={`Marge (${totals.margin_pct}%)`} value={fmtEur(totals.total_margin)} icon={TrendingUp} accent="text-emerald-600" testid="kpi-margin" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Jobs per month */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-md card-subtle p-6" data-testid="chart-jobs-per-month">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-base font-medium text-slate-900 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-[#0F4C81]" /> Aufträge pro Monat
            </h3>
            <span className="text-xs text-slate-500">Letzte {months} Monate</span>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={monthsData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="label" stroke={SLATE} fontSize={11} />
              <YAxis stroke={SLATE} fontSize={11} allowDecimals={false} />
              <Tooltip
                contentStyle={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '6px', fontSize: '12px' }}
                cursor={{ fill: '#F1F5F9' }}
              />
              <Bar dataKey="jobs" fill={PRIMARY} radius={[4, 4, 0, 0]} name="Aufträge" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Service Type Distribution */}
        <div className="bg-white border border-slate-200 rounded-md card-subtle p-6" data-testid="chart-service-distribution">
          <h3 className="font-heading text-base font-medium text-slate-900 mb-4 flex items-center gap-2">
            <PieIcon className="w-4 h-4 text-[#0F4C81]" /> Einsatzarten
          </h3>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie
                data={serviceDistData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={80}
                label={(entry) => entry.name}
                labelLine={false}
              >
                {serviceDistData.map((_, i) => (
                  <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '6px', fontSize: '12px' }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Margin development */}
      <div className="bg-white border border-slate-200 rounded-md card-subtle p-6 mb-6" data-testid="chart-margin">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-base font-medium text-slate-900 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[#0F4C81]" /> Margenentwicklung
          </h3>
          <div className="flex items-center gap-3 text-xs">
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-[#0F4C81]"></span> Umsatz</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded" style={{ background: WARNING }}></span> Kosten</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded" style={{ background: SUCCESS }}></span> Marge</span>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={monthsData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
            <XAxis dataKey="label" stroke={SLATE} fontSize={11} />
            <YAxis stroke={SLATE} fontSize={11} tickFormatter={(v) => `${v}€`} />
            <Tooltip
              contentStyle={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '6px', fontSize: '12px' }}
              formatter={(v) => fmtEur(v)}
            />
            <Line type="monotone" dataKey="revenue" stroke={PRIMARY} strokeWidth={2} dot={{ r: 4 }} name="Umsatz" />
            <Line type="monotone" dataKey="cost" stroke={WARNING} strokeWidth={2} dot={{ r: 4 }} name="Kosten" />
            <Line type="monotone" dataKey="margin" stroke={SUCCESS} strokeWidth={2} dot={{ r: 4 }} name="Marge" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Top Languages */}
        <div className="bg-white border border-slate-200 rounded-md card-subtle p-6" data-testid="chart-top-languages">
          <h3 className="font-heading text-base font-medium text-slate-900 mb-4">Top Sprachkombinationen</h3>
          {top_languages.length === 0 ? (
            <div className="text-sm text-slate-400 text-center py-8">Keine Daten</div>
          ) : (
            <div className="space-y-2">
              {top_languages.map((l, i) => {
                const max = top_languages[0].count;
                const pct = (l.count / max) * 100;
                return (
                  <div key={l.pair} className="flex items-center gap-3 text-sm" data-testid={`top-lang-${i}`}>
                    <div className="w-6 text-xs text-slate-400 font-mono text-right">{i + 1}.</div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-slate-700">{l.pair}</span>
                        <span className="font-mono text-xs text-slate-500">{l.count}</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-[#0F4C81] rounded-full transition-all" style={{ width: `${pct}%` }}></div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Status Distribution */}
        <div className="bg-white border border-slate-200 rounded-md card-subtle p-6" data-testid="chart-status">
          <h3 className="font-heading text-base font-medium text-slate-900 mb-4">Status-Verteilung</h3>
          {statusDistData.length === 0 ? (
            <div className="text-sm text-slate-400 text-center py-8">Keine Daten</div>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(220, statusDistData.length * 30)}>
              <BarChart data={statusDistData} layout="vertical" margin={{ left: 10, right: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                <XAxis type="number" stroke={SLATE} fontSize={11} allowDecimals={false} />
                <YAxis dataKey="name" type="category" stroke={SLATE} fontSize={11} width={140} />
                <Tooltip
                  contentStyle={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '6px', fontSize: '12px' }}
                  cursor={{ fill: '#F1F5F9' }}
                />
                <Bar dataKey="value" fill={PRIMARY} radius={[0, 4, 4, 0]}>
                  {statusDistData.map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Top Customers and Interpreters */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-md card-subtle p-6" data-testid="top-customers">
          <h3 className="font-heading text-base font-medium text-slate-900 mb-4 flex items-center gap-2">
            <Users className="w-4 h-4 text-[#0F4C81]" /> Top Kunden
          </h3>
          {top_customers.length === 0 ? (
            <div className="text-sm text-slate-400 text-center py-8">Keine Daten</div>
          ) : (
            <div className="space-y-2">
              {top_customers.map((c, i) => (
                <div key={c.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                  <div className="flex items-center gap-3">
                    <div className="w-6 text-xs text-slate-400 font-mono text-right">{i + 1}.</div>
                    <span className="text-sm text-slate-700">{c.name}</span>
                  </div>
                  <span className="text-sm font-mono text-slate-900">{c.count} Aufträge</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-md card-subtle p-6" data-testid="top-interpreters">
          <h3 className="font-heading text-base font-medium text-slate-900 mb-4 flex items-center gap-2">
            <UserCog className="w-4 h-4 text-[#0F4C81]" /> Top Dolmetscher
          </h3>
          {top_interpreters.length === 0 ? (
            <div className="text-sm text-slate-400 text-center py-8">Keine Daten</div>
          ) : (
            <div className="space-y-2">
              {top_interpreters.map((c, i) => (
                <div key={c.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                  <div className="flex items-center gap-3">
                    <div className="w-6 text-xs text-slate-400 font-mono text-right">{i + 1}.</div>
                    <span className="text-sm text-slate-700">{c.name}</span>
                  </div>
                  <span className="text-sm font-mono text-slate-900">{c.count} Aufträge</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default AdminReports;
